<script setup lang="ts">
/**
 * Single-select that renders the native Nuxt UI dropdown on desktop (md+) and a
 * bottom-screen sheet on mobile (<md) — the "SELECT TYPE" picker from the
 * mobile design. Drop-in replacement for <USelect> / <USelectMenu>: it forwards
 * the same props/attrs/v-model, so swapping `<USelect …/>` → `<AppSelect …/>`
 * needs no other change. Pass `menu` to back it with USelectMenu (value-key /
 * searchable) instead of USelect.
 *
 * Why render BOTH and toggle with CSS (max-md:hidden / md:hidden) rather than a
 * JS media query: it avoids an SSR/hydration mismatch and a flash — the correct
 * one is shown by the breakpoint from first paint. The bottom sheet only ever
 * opens from the mobile trigger, so on desktop it stays inert.
 */
import { computed, ref, useAttrs } from 'vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  modelValue?: any
  items?: any[]
  placeholder?: string
  disabled?: boolean
  /** Label field on object items (USelect's option-attribute). */
  optionAttribute?: string
  /** Value field on object items (USelect's value-attribute). */
  valueAttribute?: string
  /** Value field for USelectMenu-style items (value-key). */
  valueKey?: string
  size?: string
  ui?: Record<string, any>
  /** Heading shown atop the mobile bottom sheet, e.g. "Select Type". */
  title?: string
  /** Back the desktop control with USelectMenu instead of USelect. */
  menu?: boolean
}>(), {
  optionAttribute: 'label',
  title: 'Select'
})

const emit = defineEmits<{ (e: 'update:modelValue', v: any): void }>()
const attrs = useAttrs()
const open = ref(false)

// Let the desktop dropdown menu grow to fit its widest option (plus the
// selected-row check icon) instead of being pinned to the trigger width. Nuxt
// UI's default content class is `w-(--reka-select-trigger-width)`, which clips
// long labels like "MANAGEMENT FEE" once the check mark takes its space. We
// keep the trigger width as the MINIMUM so the menu never narrows. A caller's
// own `ui.content` is appended last so it still wins.
const selectUi = computed<Record<string, any>>(() => ({
  ...(props.ui || {}),
  content: ['w-fit min-w-(--reka-select-trigger-width)', props.ui?.content]
    .filter(Boolean)
    .join(' ')
}))

// Which object key holds the value — mirrors how the call site configured the
// underlying USelect/USelectMenu (value-key wins, then value-attribute, then the
// conventional `value`). Plain string/number items ARE the value.
const valueField = computed(() => props.valueKey || props.valueAttribute || 'value')

function itemValue(item: any) {
  if (item == null || typeof item !== 'object') return item
  const v = item[valueField.value]
  return v !== undefined ? v : item
}
function itemLabel(item: any) {
  if (item == null || typeof item !== 'object') return String(item ?? '')
  return item[props.optionAttribute] ?? item.label ?? String(itemValue(item))
}
function itemDisabled(item: any) {
  return !!(item && typeof item === 'object' && item.disabled)
}

const selectedLabel = computed(() => {
  const match = (props.items || []).find(i => itemValue(i) === props.modelValue)
  return match ? itemLabel(match) : (props.placeholder || props.title || 'Select')
})
const hasSelection = computed(() =>
  (props.items || []).some(i => itemValue(i) === props.modelValue)
)

function onPick(item: any) {
  if (itemDisabled(item)) return
  emit('update:modelValue', itemValue(item))
  open.value = false
}
</script>

<template>
  <!-- Desktop (md+): the native control, behaviour unchanged. The call site's
       class/extra attrs fall through via v-bind="attrs". -->
  <USelectMenu
    v-if="menu"
    :model-value="modelValue"
    :items="items"
    :placeholder="placeholder"
    :disabled="disabled"
    :value-key="valueKey"
    :size="size"
    :ui="selectUi"
    class="max-md:hidden"
    v-bind="attrs"
    @update:model-value="(v: any) => emit('update:modelValue', v)"
  />
  <USelect
    v-else
    :model-value="modelValue"
    :items="items"
    :placeholder="placeholder"
    :disabled="disabled"
    :option-attribute="optionAttribute"
    :value-attribute="valueAttribute"
    :size="size"
    :ui="selectUi"
    class="max-md:hidden"
    v-bind="attrs"
    @update:model-value="(v: any) => emit('update:modelValue', v)"
  />

  <!-- Mobile (<md): a trigger styled like the select (inherits the call site's
       class) that opens the bottom sheet. -->
  <button
    type="button"
    :disabled="disabled"
    class="md:hidden flex items-center justify-between gap-2 px-3 bg-white border border-[#C9D3DD] disabled:opacity-50 disabled:cursor-not-allowed"
    :class="hasSelection ? '' : 'text-gray-400'"
    v-bind="attrs"
    @click="open = true"
  >
    <span class="truncate">{{ selectedLabel }}</span>
    <UIcon name="i-lucide-chevron-down" class="size-4 shrink-0 text-gray-500" />
  </button>

  <!-- Bottom sheet — the mobile picker. Only ever opened from the mobile
       trigger above, so it's inert on desktop. -->
  <USlideover
    v-model:open="open"
    :title="title"
    side="bottom"
    :ui="{ content: 'max-h-[85vh] rounded-t-2xl' }"
  >
    <template #body>
      <div class="flex flex-col">
        <button
          v-for="(item, i) in items"
          :key="i"
          type="button"
          :disabled="itemDisabled(item)"
          class="w-full flex items-center justify-between gap-3 px-2 py-4 text-left text-base uppercase font-bold tracking-wide border-b border-gray-100 last:border-0 disabled:opacity-40"
          :class="itemValue(item) === modelValue ? 'text-[#284E36]' : 'text-black'"
          @click="onPick(item)"
        >
          <span class="truncate">{{ itemLabel(item) }}</span>
          <UIcon
            v-if="itemValue(item) === modelValue"
            name="i-lucide-check"
            class="size-5 shrink-0 text-[#284E36]"
          />
        </button>
      </div>
    </template>
  </USlideover>
</template>
